import { LinearGradient } from "expo-linear-gradient";
import React from "react";
import {
  Animated,
  Easing,
  Platform,
  StyleSheet,
  View,
} from "react-native";
import { YStack } from "tamagui";

const BAR_W = 256;
const SHIMMER_W = 120;
const BAR_H = 6;
const R = BAR_H / 2;

export type CoursePageLoaderVariant =
  | "course"
  | "lessons"
  | "lesson"
  | "sso"
  | "auth"
  | "coursesList"
  | "pathway";

const A11Y: Record<CoursePageLoaderVariant, string> = {
  course: "Loading your course",
  lessons: "Loading lessons in this section",
  lesson: "Loading lesson",
  sso: "Signing you in",
  auth: "Loading",
  coursesList: "Loading courses",
  pathway: "Loading pathway",
};

function prefersReducedMotion(): boolean {
  if (Platform.OS === "web" && typeof window !== "undefined") {
    return (
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false
    );
  }
  return false;
}

export type CoursePagePremiumLoaderProps = {
  variant?: CoursePageLoaderVariant;
};

/** Slim pill bar: warm track, soft shadow, smooth shimmer — still one control only. */
export function CoursePagePremiumLoader({
  variant = "course",
}: CoursePagePremiumLoaderProps) {
  const reduceMotion = prefersReducedMotion();
  const useDriver = Platform.OS !== "web";
  const t = React.useRef(new Animated.Value(0)).current;

  React.useEffect(() => {
    if (reduceMotion) {
      t.setValue(0.5);
      return;
    }
    const loop = Animated.loop(
      Animated.timing(t, {
        toValue: 1,
        duration: 1580,
        easing: Easing.bezier(0.45, 0.05, 0.55, 0.95),
        useNativeDriver: useDriver,
      }),
      { resetBeforeIteration: true },
    );
    loop.start();
    return () => loop.stop();
  }, [reduceMotion, t, useDriver]);

  const x = t.interpolate({
    inputRange: [0, 1],
    outputRange: [-SHIMMER_W, BAR_W + 16],
  });

  return (
    <YStack
      w="100%"
      maw={1140}
      als="center"
      px="$4"
      py="$8"
      jc="center"
      ai="center"
      accessibilityRole="progressbar"
      accessibilityLabel={A11Y[variant]}
    >
      <View style={styles.shadowWrap}>
        <LinearGradient
          colors={["#F3F0EC", "#E5DFD8"]}
          locations={[0, 1]}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={styles.trackFace}
        >
          {!reduceMotion ? (
            <Animated.View
              style={[
                styles.shimmer,
                {
                  width: SHIMMER_W,
                  transform: [{ translateX: x }],
                },
              ]}
            >
              <LinearGradient
                colors={[
                  "rgba(255,255,255,0)",
                  "rgba(255,255,255,0.88)",
                  "rgba(255, 245, 240, 0.95)",
                  "rgba(228, 87, 46, 0.38)",
                  "rgba(255,255,255,0)",
                ]}
                locations={[0, 0.28, 0.42, 0.55, 1]}
                start={{ x: 0, y: 0.5 }}
                end={{ x: 1, y: 0.5 }}
                style={StyleSheet.absoluteFillObject}
              />
            </Animated.View>
          ) : (
            <View style={styles.reduceFill} />
          )}
        </LinearGradient>
      </View>
    </YStack>
  );
}

const styles = StyleSheet.create({
  shadowWrap: {
    borderRadius: R,
    shadowColor: "#2C221C",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.09,
    shadowRadius: 12,
    elevation: 4,
  },
  trackFace: {
    width: BAR_W,
    height: BAR_H,
    borderRadius: R,
    overflow: "hidden",
  },
  shimmer: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
  },
  reduceFill: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(228, 87, 46, 0.22)",
    borderRadius: R,
  },
});
